import React, { useEffect, useState } from "react";
import { Breadcrumb, Layout, Table, Button, Input, Space, Tag, Card, Typography, notification, DatePicker, Spin, Modal } from "antd";
import { useAuth } from "../../hooks/useAuth";
import { FormattedMessage, useIntl } from "react-intl";
import { SearchOutlined, EyeOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from "react-router-dom";
import { IEvent } from "../../services/nestjs-base-api/types";
import { useGetOrganisationByIdQuery, useGetAdminOrganisationEventsQuery } from "../../services/nestjs-base-api/admin-api";
import { GeneralStatusEnum } from "../../enums/GeneralStatusEnum";
import { Utils } from "../../utils/utils";
import dayjs from 'dayjs';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

const AdminOrganisationEventsPage: React.FunctionComponent = () => {
  const intl = useIntl();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { organisationId } = useParams<{ organisationId: string }>();
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  
  // Fetch organisation details
  const {
    data: organisation,
    error: organisationError,
    isLoading: isLoadingOrganisation
  } = useGetOrganisationByIdQuery({
    jwtToken: user.jwtToken,
    organisationId: Number(organisationId)
  }, { skip: !organisationId });
  
  // Fetch organisation events
  const {
    data: events,
    error: eventsError,
    isLoading: isLoadingEvents
  } = useGetAdminOrganisationEventsQuery({
    jwtToken: user.jwtToken,
    organisationId: Number(organisationId)
  }, { skip: !organisationId });

  useEffect(() => {
    if (organisationError || eventsError) {
      if (
        (organisationError && 'status' in organisationError && organisationError.status === 401) ||
        (eventsError && 'status' in eventsError && eventsError.status === 401)
      ) {
        logout();
      }
    }
  }, [organisationError, eventsError, logout]);

  // Filter events based on search, date range, and status
  const filteredEvents = events ? events.filter((event: IEvent) => {
    // Filter by search text
    const matchesSearch = searchText ? 
      event.name.toLowerCase().includes(searchText.toLowerCase()) ||
      (event.description && event.description.toLowerCase().includes(searchText.toLowerCase())) :
      true;
    
    // Filter by date range
    const eventDate = dayjs(event.date);
    const matchesDateRange = dateRange && dateRange[0] && dateRange[1] ? 
      eventDate.isAfter(dateRange[0]) && eventDate.isBefore(dateRange[1]) :
      true;
    
    // Filter by status
    const matchesStatus = statusFilter ? 
      event.status === statusFilter :
      true;
    
    return matchesSearch && matchesDateRange && matchesStatus;
  }) : [];

  // Navigate back to organisations
  const goBackToOrganisations = () => {
    navigate('/admin/organisations');
  };

  // Handle event view
  const handleViewEvent = (id: number) => {
    navigate(`/admin/organisations/${organisationId}/events/${id}`);
  };

  // Handle event deletion with confirmation
  const handleDeleteEvent = (id: number, name: string) => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'events.delete.confirm.title' }) || 'Delete Event',
      content: (
        <>
          <p>
            {intl.formatMessage({ id: 'events.delete.confirm.content' }) ||
              'This will permanently delete the event, all its galleries, and all photos. This action cannot be undone.'}
          </p>
          <p style={{ fontWeight: 'bold' }}>"{name}"</p>
        </>
      ),
      okText: intl.formatMessage({ id: 'common.delete' }) || 'Delete',
      cancelText: intl.formatMessage({ id: 'common.cancel' }) || 'Cancel',
      okButtonProps: { danger: true },
      onOk: () => {
        // In a real implementation, this would call the API to delete the event
        notification.success({
          message: intl.formatMessage({ id: 'admin.events.deleteSuccess' }) || 'Event Deleted',
          description: intl.formatMessage(
            { id: 'admin.events.deleteSuccessDesc' },
            { name }
          ) || `Event "${name}" has been successfully deleted.`,
        });
      },
    });
  };

  // Table columns
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a: IEvent, b: IEvent) => a.id - b.id,
      width: 80,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: IEvent, b: IEvent) => a.name.localeCompare(b.name),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
      sorter: (a: IEvent, b: IEvent) => dayjs(a.date).unix() - dayjs(b.date).unix(),
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: (location: string) => location || '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: GeneralStatusEnum) => (
        <Tag color={status === GeneralStatusEnum.Active ? 'green' : 'red'}>
          {Utils.readableGeneralStatus(status)}
        </Tag>
      ),
    },
    {
      title: 'Galleries',
      key: 'galleries',
      render: (_: any, record: IEvent) => (
        <Button 
          type="link" 
          onClick={() => navigate(`/admin/organisations/${organisationId}/events/${record.id}/galleries`)}
        >
          View Galleries
        </Button>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: IEvent) => (
        <Space size="small">
          <Button 
            icon={<EyeOutlined />} 
            onClick={() => handleViewEvent(record.id)} 
            title="View"
          />
          <Button 
            danger 
            icon={<DeleteOutlined />} 
            onClick={() => handleDeleteEvent(record.id, record.name)} 
            title="Delete"
          />
        </Space>
      ),
    },
  ];

  // Loading state
  if (isLoadingOrganisation) {
    return (
      <Content style={{ margin: '0 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
          <Spin size="large" />
          <Text style={{ marginLeft: 16 }}>Loading organisation details...</Text>
        </div>
      </Content>
    );
  }

  return (
    <Content style={{ margin: '0 16px' }}>
      <Breadcrumb style={{ margin: '16px 0' }}>
        <Breadcrumb.Item>RecogniX</Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'section.adminDashboardName'} defaultMessage="Admin Dashboard" />
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <a onClick={goBackToOrganisations}>
            <FormattedMessage id={'section.organisationsName'} defaultMessage="Organisations" />
          </a>
        </Breadcrumb.Item>
        <Breadcrumb.Item>{organisation?.name}</Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'section.eventsName'} defaultMessage="Events" />
        </Breadcrumb.Item>
      </Breadcrumb>
      
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button 
              icon={<ArrowLeftOutlined />} 
              style={{ marginRight: 16 }} 
              onClick={goBackToOrganisations}
            >
              Back to Organisations
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              {organisation?.name} - <FormattedMessage id={'admin.events.title'} defaultMessage="Events" />
            </Title>
          </div>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <Search
              placeholder={intl.formatMessage({ id: 'admin.events.searchPlaceholder' }) || "Search events by name or description"}
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={value => setSearchText(value)}
              style={{ width: 300 }}
            />
            
            <Space>
              <RangePicker 
                onChange={(dates) => setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null])}
                placeholder={[
                  intl.formatMessage({ id: 'admin.events.startDate' }) || "Start Date",
                  intl.formatMessage({ id: 'admin.events.endDate' }) || "End Date"
                ]}
              />
              
              <Button
                type="primary"
                onClick={() => navigate(`/dashboard/events/create?organisationId=${organisationId}`)}
              >
                Create Event
              </Button>
            </Space>
          </div>
          
          <Table 
            dataSource={filteredEvents} 
            columns={columns} 
            rowKey="id"
            loading={isLoadingEvents}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: "No events found for this organisation" }}
          />
        </Space>
      </Card>
    </Content>
  );
};

export default AdminOrganisationEventsPage;